import { useMemo, useState } from 'react';
import { articleCategories, articles } from '@/mocks/home';

export default function ArticlesSection() {
  const [activeKey, setActiveKey] = useState('all');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return articles.filter((article) => {
      const matchCategory = activeKey === 'all' || article.categoryKey === activeKey;
      if (!matchCategory) return false;
      if (!kw) return true;
      const haystack = `${article.title} ${article.excerpt} ${article.tags.join(' ')}`.toLowerCase();
      return haystack.includes(kw);
    });
  }, [activeKey, keyword]);

  return (
    <section id="articles" className="bg-background-100 paper-grain py-14 md:py-20">
      <div className="mx-auto max-w-8xl px-4 md:px-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] text-primary-600">
              <i className="ri-bookmark-3-line"></i>
              FEATURED
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              精选深度内容
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              每一篇都从具体环节切入，不用结论代替过程。切换分类或直接搜索你关心的话题。
            </p>
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              type="search"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索标题 / 标签"
              aria-label="搜索精选内容"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/25"
            />
          </div>
        </div>

        <div
          className="no-scrollbar mt-7 flex gap-1 overflow-x-auto rounded-full border border-background-300/70 bg-background-50 p-1"
          role="tablist"
          aria-label="内容分类筛选"
        >
          {articleCategories.map((tab) => {
            const active = tab.key === activeKey;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveKey(tab.key)}
                className={`shrink-0 rounded-full px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-primary-500 font-medium text-background-50'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((article) => (
              <article key={article.id} className="h-full">
                <a
                  href="#spotlight"
                  className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-300/70 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <div className="relative h-48 w-full overflow-hidden">
                    <img
                      src={article.image}
                      alt={`${article.title}配图`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                    <span className="absolute left-3 top-3 rounded-md bg-background-50/92 px-2.5 py-1 text-xs font-medium text-primary-700">
                      {article.categoryName}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-600 md:text-lg">
                      {article.title}
                    </h3>
                    <p className="mt-2.5 flex-1 text-sm leading-relaxed text-foreground-600">
                      {article.excerpt}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {article.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4 text-xs text-foreground-500">
                      <span className="truncate">{article.author}</span>
                      <span className="inline-flex shrink-0 items-center gap-2">
                        <span>{article.date}</span>
                        <span className="h-3 w-px bg-background-300"></span>
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </span>
                    </div>
                  </div>
                </a>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-file-search-line"></i>
            </span>
            <h3 className="mt-4 font-heading text-lg font-bold text-foreground-900">
              没有找到相关内容
            </h3>
            <p className="mt-2 text-sm text-foreground-600">
              换个关键词试试，或者切换其他主题分类。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActiveKey('all');
              }}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}